import { FunctionComponent, ReactNode } from "react";

import {
	faBoltLightning,
	faCircleCheck,
	faGraduationCap,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components";

interface BriefingStripProps {
	repliesTotal: number;
	newDraftsTotal: number;
	reEngageTotal: number;
	onboardingDone: number;
	onboardingTotal: number;
	issuesCount: number;
}

interface SegmentProps {
	icon: IconDefinition;
	accent: string;
	label: string;
	children: ReactNode;
}

const Segment: FunctionComponent<SegmentProps> = ({ icon, accent, label, children }) => (
	<div className="flex items-center gap-2 px-3 first:pl-0 border-r border-divider last:border-r-0">
		<div
			className={classNames("w-6 h-6 rounded-md flex items-center justify-center shrink-0", accent)}
		>
			<Icon className="w-3.5 h-3.5" icon={icon} />
		</div>
		<div className="min-w-0">
			<div className="text-[11px] font-semibold uppercase tracking-wide text-text-meta">
				{label}
			</div>
			<div className="text-xs text-text">{children}</div>
		</div>
	</div>
);

const Num: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
	<span className="font-semibold text-text">{children}</span>
);

const Sep: FunctionComponent = () => <span className="mx-1.5 text-text-meta">·</span>;

const formatTotal = (n: number) => (n >= 1000 ? "1000+" : String(n));

export const BriefingStrip: FunctionComponent<BriefingStripProps> = ({
	repliesTotal,
	newDraftsTotal,
	reEngageTotal,
	onboardingDone,
	onboardingTotal,
	issuesCount,
}) => {
	const hasIssues = issuesCount > 0;

	return (
		<div className="bg-white border border-border-light rounded-xl px-4 py-3 mb-5">
			<div className="flex items-center flex-wrap gap-y-2">
				<Segment
					accent="bg-status-success-light text-status-success"
					icon={faBoltLightning}
					label="Action required"
				>
					<Num>{formatTotal(repliesTotal)}</Num> replies
					<Sep />
					<Num>{formatTotal(newDraftsTotal)}</Num> new leads
					<Sep />
					<Num>{formatTotal(reEngageTotal)}</Num> to re-engage
				</Segment>
				<Segment
					accent="bg-action-200 text-brand-light"
					icon={faGraduationCap}
					label="Onboarding progress"
				>
					<Num>{onboardingDone}</Num> of {onboardingTotal} done
				</Segment>
				<Segment
					accent={
						hasIssues
							? "bg-status-error-light text-status-error"
							: "bg-status-success-light text-status-success"
					}
					icon={hasIssues ? faTriangleExclamation : faCircleCheck}
					label={hasIssues ? "Issues found" : "No issues"}
				>
					{hasIssues ? (
						<>
							<Num>{issuesCount}</Num> to look at
						</>
					) : (
						"All clear"
					)}
				</Segment>
			</div>
		</div>
	);
};
